import type React from 'react';
import type { TypeaheadMenuProps, TypeaheadResult } from 'react-bootstrap-typeahead';

import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import Select from '@web/components/select';
import { groupBy } from 'lodash';
import { Fragment, useCallback, useMemo } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { AsyncTypeahead, Highlighter, Menu, MenuItem } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { FailCodeSelection, FailCodeVehicleParams } from './fail-codes-modal.types';

import { type FailCodeSearchOption, useSearchByFailCode } from './hooks/use-search-by-fail-code';

/**
 * Props for the {@link SearchByFailCode} component.
 */
export type SearchByFailCodeProps = {
    /** Callback invoked when the user clicks the back button to return to the search method selector. */
    onBack: () => void;

    /** Callback invoked when the user closes the modal. */
    onClose: () => void;

    /** Callback invoked when the user confirms a complete fail code selection. */
    onConfirmFailCode: (selection: FailCodeSelection) => void;

    /** Vehicle params used to scope fail code lookups, already mapped from VIN data by the parent. */
    vehicleParams: FailCodeVehicleParams;
};

/**
 * Search-by-fail-code view within the fail codes modal. Users type a search query to find
 * fail codes, select one from the results, then choose a section and subsection (which may
 * be auto-selected if only one option exists). Renders a typeahead for searching and select
 * dropdowns for section/subsection disambiguation.
 *
 * @param props - {@link SearchByFailCodeProps}
 * @returns the search by fail code JSX
 */
const useSearchByFailCodeStyles = createUseStyles({
    buttonRow: {
        display: 'flex',
        gap: 15,
    },
});

/**
 * SearchByFailCode component renders the fail code search flow by direct fail code lookup.
 * Allows the user to search, select, and confirm a fail code.
 *
 * @param props - SearchByFailCodeProps
 * @returns JSX.Element
 */
export function SearchByFailCode({ onBack, onClose, onConfirmFailCode, vehicleParams }: SearchByFailCodeProps) {
    // Hooks
    const classes = useSearchByFailCodeStyles();
    const { t } = useTranslation();

    // Self-contained read-only exception: this hook qualifies because (1) it only performs
    // read-only fail code lookups, (2) the parent does not participate in any intermediate
    // cascade state, and (3) the parent receives the final result via onConfirmFailCode.
    const {
        confirmSelection,
        handleFailCodeSelect,
        handleSearch,
        handleSectionChange,
        handleSubsectionChange,
        hasSelectedFailCode,
        isConfirmDisabled,
        isResolving,
        isSearching,
        searchInputRef,
        searchOptions,
        sectionOptions,
        selectedFailCode,
        selectedSection,
        selectedSubsection,
        subsectionOptions,
    } = useSearchByFailCode(vehicleParams);

    // Handlers

    /**
     * Handle the user selecting a fail code from the typeahead dropdown. Extracts the first
     * selected item and forwards it to the hook's handler.
     *
     * @param selected - array of selected options from the typeahead (always 0 or 1 items)
     */
    const handleTypeaheadChange = useCallback(
        (selected: FailCodeSearchOption[]) => {
            handleFailCodeSelect(selected[0] ?? null);
        },
        [handleFailCodeSelect]
    );

    /**
     * Handle the user typing into the search input. Hides the typeahead menu while the user
     * is still typing to avoid showing stale results.
     */
    const handleSearchInputChange = useCallback(() => {
        searchInputRef.current?.hideMenu();
    }, [searchInputRef]);

    /**
     * Handle the user changing the section dropdown value.
     *
     * @param event - the change event from the select element
     */
    const handleSectionSelectChange = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            handleSectionChange((event.target as HTMLSelectElement).value);
        },
        [handleSectionChange]
    );

    /**
     * Handle the user changing the subsection dropdown value.
     *
     * @param event - the change event from the select element
     */
    const handleSubsectionSelectChange = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            handleSubsectionChange((event.target as HTMLSelectElement).value);
        },
        [handleSubsectionChange]
    );

    /**
     * Handle the user clicking the confirm button. Builds the final selection from the
     * hook state and passes it to the parent callback, then closes the modal.
     */
    const handleConfirmButtonClick = useCallback(() => {
        const selection = confirmSelection();
        if (!selection) {
            return;
        }
        onConfirmFailCode(selection);
        onClose();
    }, [confirmSelection, onClose, onConfirmFailCode]);

    // Derived state

    /** Section options with a placeholder prepended for the select dropdown. */
    const sectionOptionsWithPlaceholder = useMemo(
        () => [
            { title: t('modals:failCodeSearch.failCodeSectionPlaceholder', 'Select section'), value: '' },
            ...sectionOptions,
        ],
        [sectionOptions, t]
    );

    /** Subsection options with a placeholder prepended for the select dropdown. */
    const subsectionOptionsWithPlaceholder = useMemo(
        () => [
            {
                title: t('modals:failCodeSearch.failCodeSubSectionPlaceholder', 'Select subsection'),
                value: '',
            },
            ...subsectionOptions,
        ],
        [subsectionOptions, t]
    );

    /** The currently selected items for the typeahead's `selected` prop. */
    const typeaheadSelected = useMemo(() => (selectedFailCode ? [selectedFailCode] : []), [selectedFailCode]);

    /**
     * Render the custom dropdown menu for the fail code typeahead. Groups results by category,
     * shows a "no matches" state with a manual entry button when no results exist, and renders
     * highlighted result items within section headers.
     *
     * Exception: this is defined as a local function rather than extracted to a separate component
     * because the react-bootstrap-typeahead `renderMenu` prop API requires a render callback with
     * a fixed signature `(results, menuProps, state)`, and the callback needs access to `t` from
     * the component scope.
     *
     * @param results - the typeahead result items to render
     * @param menuProps - props to spread onto the Menu component
     * @param state - the typeahead state containing the current search text
     * @returns the rendered menu JSX
     */
    const failCodeMenuRenderer = useCallback(
        (
            results: Array<TypeaheadResult<FailCodeSearchOption>>,
            menuProps: TypeaheadMenuProps<FailCodeSearchOption>,
            state: { /** The current search input text. */ text: string }
        ): React.ReactElement => {
            let index = 0;

            // Group results by category, filtering out the typeahead library's pagination objects
            const sections = groupBy(results, (item) => {
                if (item.failCode) {
                    return t('modals:failCodeSearch.resultSections.failCodes', 'Fail Code');
                }
                return 'unknown';
            });

            const items = Object.keys(sections)
                .filter((name) => name !== 'unknown')
                .sort()
                .map((sectionName) => {
                    return (
                        <Fragment key={sectionName}>
                            {index !== 0 && <Menu.Divider />}

                            <Menu.Header>
                                <Typography variant="label">{sectionName}</Typography>
                            </Menu.Header>

                            {sections[sectionName].map((item) => {
                                const menuItem = (
                                    <MenuItem key={index} option={item} position={index}>
                                        <Highlighter search={state.text}>{item.label}</Highlighter>
                                    </MenuItem>
                                );
                                index += 1;
                                return menuItem;
                            })}
                        </Fragment>
                    );
                });

            // Show "no matches" when no results were found
            if (!items.length) {
                return (
                    <Menu {...menuProps}>
                        <div className="px-4 py-3">
                            <Typography color="grayFive" variant="lead">
                                {t('modals:failCodeSearch.noMatchesFound', 'No matches found.')}
                            </Typography>
                        </div>
                    </Menu>
                );
            }

            return <Menu {...menuProps}>{items}</Menu>;
        },
        [t]
    );

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{t('modals:failCodeSearch.title', 'Fail Code Search')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Row>
                    <Col>
                        <AsyncTypeahead
                            filterBy={filterByPassthrough}
                            id="fail-code-search"
                            isLoading={isSearching}
                            labelKey={getFailCodeLabel}
                            onChange={handleTypeaheadChange}
                            onInputChange={handleSearchInputChange}
                            onSearch={handleSearch}
                            options={searchOptions}
                            placeholder={t('modals:failCodeSearch.searchPlacehodler', 'Search')}
                            ref={searchInputRef}
                            renderMenu={failCodeMenuRenderer}
                            selected={typeaheadSelected}
                            useCache={false}
                        />
                    </Col>
                </Row>

                <hr className="my-4" />

                <Row>
                    {hasSelectedFailCode ? (
                        <Col>
                            <div className="mb-4">
                                <Typography color="textDarkSecondary" variant="lead">
                                    {t(
                                        'modals:failCodeSearch.multipleMatchInstructions',
                                        'Your search returned multiple options. Please select the section and subsection below to specify:'
                                    )}
                                </Typography>
                            </div>

                            <div className="mb-4">
                                <Label>{t('modals:failCodeSearch.sectionLabel', 'Section')}</Label>
                                <Select
                                    onChange={handleSectionSelectChange}
                                    options={sectionOptionsWithPlaceholder}
                                    value={JSON.stringify(selectedSection)}
                                />
                            </div>

                            <Label>{t('modals:failCodeSearch.subsectionLabel', 'Subsection')}</Label>
                            <Select
                                disabled={!selectedSection}
                                onChange={handleSubsectionSelectChange}
                                options={subsectionOptionsWithPlaceholder}
                                value={JSON.stringify(selectedSubsection)}
                            />
                        </Col>
                    ) : (
                        <Col>
                            <Typography color="textDarkSecondary" variant="lead">
                                {t(
                                    'modals:failCodeSearch.instructions.title',
                                    'Use the search bar above to find a fail code by entering any of the following:'
                                )}
                            </Typography>

                            <div className="mt-2">
                                <Typography color="textDarkSecondary" variant="lead">
                                    {t(
                                        'modals:failCodeSearch.instructions.steps',
                                        '1. Fail Code\r\n2. Fail Code description'
                                    )}
                                </Typography>
                            </div>
                        </Col>
                    )}
                </Row>
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button iconLeft={<Icon color="blueOne" name="chevron-left" />} onPress={onBack} variant="ghost-blue">
                    {t('modals:failCodeSearch.actions.back', 'Back')}
                </Button>

                <div className={classes.buttonRow}>
                    <Button onPress={onClose} variant="link-blue">
                        <Typography color="blueOne">{t('qmr:sections:failCode.cancel')}</Typography>
                    </Button>
                    <Button disabled={isConfirmDisabled} isLoading={isResolving} onPress={handleConfirmButtonClick}>
                        {t('modals:failCodeSearch.actions.add', 'Add Fail Code')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}

/**
 * Passthrough filter function for the AsyncTypeahead. Returns true for all items because
 * server-side filtering is already applied; client-side filtering would remove valid results.
 *
 * @returns true always
 */
function filterByPassthrough(): boolean {
    return true;
}

/**
 * Extract a display label from a fail code search option for the typeahead.
 *
 * @param option - the fail code search option
 * @returns a formatted string like "A1234 - Engine misfire"
 */
function getFailCodeLabel(option: FailCodeSearchOption): string {
    return option.label;
}
